/**
 * Shared visual styling utilities.
 *
 * These derive from the active theme's `primary` palette so the tenant's
 * brand color (set via the account API and resolved in `themeFactory`)
 * flows through to primary CTAs and progress meters. They are theme
 * functions — spread them where MUI accepts an sx callback, e.g. as an
 * entry in an sx array: `sx={[gradientPrimaryButtonSx, { px: 3 }]}`.
 */

import { Theme, alpha } from '@mui/material/styles';

/**
 * Diagonal gradient for the primary "contained" CTA. Built from the
 * theme primary palette (light → main → dark, which MUI derives from the
 * tenant's `primary.main`) so every Save / Submit / Confirm reads in the
 * tenant's brand color rather than a hardcoded one.
 *
 * Applied automatically to every `variant="contained"` primary button by
 * `components/ui/Button.tsx`; callers don't need to spread it by hand.
 */
export const gradientPrimaryButtonSx = (theme: Theme) => ({
  color: theme.palette.primary.contrastText,
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
  boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.35)}`,
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.primary.dark} 100%)`,
    boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.5)}`,
  },
});

/**
 * Horizontal gradient for the FILL of a `<LinearProgress />`
 * (`& .MuiLinearProgress-bar`). Same palette as the primary button so a
 * progress meter and the CTA next to it read as part of the same family.
 * Spread into the LinearProgress's sx alongside any sizing / radius — the
 * track color stays whatever you set (typically `grey.100`).
 */
export const gradientProgressBarSx = (theme: Theme) => ({
  '& .MuiLinearProgress-bar': {
    background: `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
  },
});
